import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Play } from 'lucide-react';
import { Skeleton, toast } from '@mlm/ui';
import { courseBadge } from '@mlm/shared';
import {
  getLearningDashboard,
  getMyLeads,
  getReferralCode,
  getSlotWallet,
  getTenantConfig,
  listAnnouncements,
  productImageUrl,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { useCourseCatalog, useProductHref } from '../storefront/lib/hooks';
import { peso } from '../storefront/lib/format';

/**
 * Learning-mode member dashboard — design_handoff_classroom_dashboard, Option
 * 1b "Classroom". Replaces the passbook body when `member_dashboard_mode ===
 * 'learning'`: greeting → "Jump back in" hero (lesson thumbnail, module-step
 * indicator, Resume) → "New in the catalog" strip → Invite-a-friend (gated by
 * `show_team_earnings`) + Announcements. Colors flow from the tenant theme
 * tokens (`bg-o`, `text-o-d`, `bg-o-l`) — no hard-coded brand hexes.
 */
export function ClassroomDashboard() {
  const user = useAuthStore((s) => s.user);
  const tenantQ = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
  const showTeam = tenantQ.data?.showTeamEarnings ?? true;
  const dashQ = useQuery({ queryKey: ['learning-dashboard'], queryFn: getLearningDashboard });
  const annQ = useQuery({ queryKey: ['announcements'], queryFn: listAnnouncements });
  const greetingName = user?.firstName ?? user?.fullName ?? user?.email ?? '';

  return (
    <div className="mx-auto max-w-[1116px] space-y-4">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.02em]">
            {greeting()}, {greetingName}
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">Pick up where you left off.</p>
        </div>
      </div>

      {dashQ.isLoading ? (
        <Skeleton className="h-[280px] rounded-xl" />
      ) : dashQ.data?.continueLearning ? (
        <JumpBackIn cl={dashQ.data.continueLearning} />
      ) : (
        <FirstCourseCta />
      )}

      <NewInCatalog />

      <div className={`grid gap-4 ${showTeam ? 'md:grid-cols-2' : ''}`}>
        {showTeam && <InviteCard />}
        <div className="rounded-xl border bg-card px-6 py-5 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Announcements
          </div>
          <div className="mt-2 space-y-2">
            {(annQ.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing new right now.</p>
            ) : (
              (annQ.data ?? []).slice(0, 3).map((a) => (
                <div key={a.id} className="flex items-baseline justify-between gap-3">
                  <span className="text-[13.5px] font-semibold">{a.title}</span>
                  {a.publishedAt && (
                    <span className="flex-none text-xs text-muted-foreground">
                      {new Date(a.publishedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

function JumpBackIn({
  cl,
}: {
  cl: NonNullable<Awaited<ReturnType<typeof getLearningDashboard>>['continueLearning']>;
}) {
  return (
    <div className="grid overflow-hidden rounded-xl border bg-card shadow-sm md:grid-cols-[58%_1fr]">
      <Link
        to={`/learn/${cl.courseId}?lesson=${cl.lessonId}`}
        className="relative flex min-h-[280px] items-center justify-center bg-o/10"
        aria-label={`Resume ${cl.lessonTitle}`}
      >
        <img
          src={productImageUrl(cl.productId, 0)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-90"
          onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
        />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-o text-white shadow-[0_4px_16px_hsl(var(--primary)/.35)]">
          <Play className="ml-0.5 h-6 w-6 fill-current" />
        </span>
        <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
          Module {cl.lessonIndex} of {cl.lessonCount}
        </span>
      </Link>
      <div className="flex flex-col px-7 py-[26px]">
        <div className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
          Jump back in
        </div>
        <h2 className="mt-2 text-xl font-bold tracking-[-0.02em]">{cl.courseTitle}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Module {cl.lessonIndex} of {cl.lessonCount} · {cl.lessonTitle}
        </p>
        {/* Module-step indicator: one segment per lesson — NOT a watch-% bar
            (progress = server-recorded lesson opens/completions). */}
        <div className="mt-4 flex gap-1" aria-hidden>
          {cl.lessonsDone.map((done, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full ${done || i < cl.lessonIndex - 1 ? 'bg-o' : 'bg-muted'}`}
            />
          ))}
        </div>
        <div className="mt-1.5 flex justify-between text-xs text-muted-foreground">
          <span>
            {cl.completedCount} of {cl.lessonCount} done
          </span>
          <span>
            Last opened{' '}
            {new Date(cl.lastOpenedAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            })}
          </span>
        </div>
        <div className="mt-auto flex items-center gap-4 pt-[18px]">
          <Link
            to={`/learn/${cl.courseId}?lesson=${cl.lessonId}`}
            className="inline-flex items-center gap-2 rounded-lg bg-o px-[18px] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-o-d"
          >
            <Play className="h-[13px] w-[13px] fill-current" /> Resume
          </Link>
          <Link
            to={`/course/${cl.productId}`}
            className="text-[13px] font-semibold text-muted-foreground hover:text-foreground"
          >
            View syllabus
          </Link>
        </div>
      </div>
    </div>
  );
}

function FirstCourseCta() {
  return (
    <div className="grid overflow-hidden rounded-xl border bg-card shadow-sm md:grid-cols-[58%_1fr]">
      <div className="flex min-h-[220px] items-center justify-center bg-o/10">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-o text-white">
          <Play className="ml-0.5 h-6 w-6 fill-current" />
        </span>
      </div>
      <div className="flex flex-col px-7 py-[26px]">
        <div className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
          Get started
        </div>
        <h2 className="mt-2 text-xl font-bold tracking-[-0.02em]">Pick your first course</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Browse the catalog and start learning at your own pace.
        </p>
        <div className="mt-auto pt-[18px]">
          <Link
            to="/courses"
            className="inline-block rounded-lg bg-o px-[18px] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-o-d"
          >
            Browse catalog
          </Link>
        </div>
      </div>
    </div>
  );
}

function NewInCatalog() {
  // The heading says "new", so the SERVER sorts by recency and returns three
  // rows — the old catalog.slice(0, 3) showed whatever the featured sort put
  // first, and called it new.
  const { tiles, isLoading } = useCourseCatalog({
    sort: 'newest',
    limit: 3,
    // A course the member already owns is not a catalog pick for them.
    excludeOwned: true,
  });
  const hrefFor = useProductHref();
  const picks = tiles;
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-base font-bold">New in the catalog</h2>
        <Link to="/courses" className="text-[13px] font-semibold text-o-d hover:underline">
          View catalog →
        </Link>
      </div>
      {!isLoading && picks.length === 0 && (
        <p className="text-sm text-muted-foreground">No courses published yet.</p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-56 rounded-[10px]" />
            ))
          : picks.map((t) => (
              <Link
                key={t.product.id}
                to={hrefFor(t.product)}
                className="overflow-hidden rounded-[10px] border bg-card shadow-sm"
              >
                <div className="aspect-video bg-o/10">
                  {t.product.image && (
                    <img src={t.product.image} alt="" className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="px-4 py-3.5">
                  <div className="text-sm font-semibold">{t.product.name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t.course.lessonCount} module{t.course.lessonCount === 1 ? '' : 's'}
                    {t.course.level ? ` · ${cap(t.course.level)}` : ''}
                  </div>
                  <div className="mt-2.5 flex items-center justify-between">
                    <span className="font-mono text-sm font-semibold">
                      {t.course.accessLevel === 'free'
                        ? 'Free'
                        : t.course.accessLevel === 'subscriber'
                          ? 'Subscribers'
                          : peso(t.product.price)}
                    </span>
                    {courseBadge(t.course) && (
                      <span
                        className={`rounded-full border px-[9px] py-0.5 text-[11px] font-semibold ${
                          courseBadge(t.course) === 'featured'
                            ? 'border-gold/50 bg-gold/20 text-[#8A6C00]'
                            : 'border-o/40 bg-o/10 text-o-d'
                        }`}
                      >
                        {courseBadge(t.course) === 'featured' ? 'Featured' : 'New'}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
      </div>
    </div>
  );
}

function InviteCard() {
  const refQ = useQuery({
    queryKey: ['referral-code'],
    queryFn: getReferralCode,
    staleTime: 60_000,
  });
  const leadsQ = useQuery({ queryKey: ['leads'], queryFn: getMyLeads, staleTime: 60_000 });
  const { activeSlotId, hasAnyActiveSlot } = useMySlots();
  const walletQ = useQuery({
    queryKey: ['wallet', activeSlotId],
    queryFn: () => getSlotWallet(activeSlotId),
    enabled: hasAnyActiveSlot,
  });
  const [copied, setCopied] = useState(false);
  const url = useMemo(() => {
    const ref = refQ.data?.shareRef;
    if (!ref) return '';
    return `${window.location.origin}/r/${encodeURIComponent(ref)}`;
  }, [refQ.data]);
  const joined = leadsQ.data?.length ?? 0;
  const rewards = walletQ.data?.lifetimeEarned ?? '0';

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select the link and copy manually.");
    }
  }

  return (
    <div className="rounded-xl border bg-card px-6 py-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Invite a friend
          </div>
          <div className="mt-1.5 text-[13px] text-muted-foreground">
            {joined} friend{joined === 1 ? '' : 's'} joined ·{' '}
            <span className="font-mono text-credit">{peso(rewards)}</span> in rewards
          </div>
        </div>
        {url && (
          <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
            <span className="max-w-[220px] truncate font-mono text-[12.5px]">{url}</span>
            <button
              onClick={copy}
              className="flex h-6 w-6 items-center justify-center rounded hover:bg-background"
              aria-label="Copy link"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
